import type { ReactNode } from 'react';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { predicateFor } from '@gonfalon/legacy-form-validation/validationUtils';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { CopyToClipboard } from '@gonfalon/ui/CopyToClipboard';
import { Button, Code } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { ExperimentApprovalResource } from '#utils/approvals/types';
import type { Environment } from '#utils/environments/records/Environment';
import type { Flag } from '#utils/flags/records/Flag';
import type { Project } from '#utils/projects/records/Project';
import type { RelayAutoConfig } from '#utils/relayAutoConfigUtils';
import type { Segment } from '#utils/segment/records/Segment';

import type { AiConfig } from './aiConfigUtils';

export const isResourceConfirmationValid = ({ input, name, key }: { input: string; name?: string; key?: string }) => {
  const s = input.trim().toLowerCase();
  const trimmedName = name?.trim().toLowerCase();
  const trimmedKey = key?.trim().toLowerCase();
  return s === trimmedName || s === trimmedKey;
};

export type ValidationEntity =
  | Environment
  | Project
  | Flag
  | Segment
  | AiConfig
  | RelayAutoConfig
  | {
      name: string;
    }
  | {
      key: string;
      name?: string;
    };

export const validation = (
  type: string,
  entity: ValidationEntity,
  label?: ReactNode,
): { label: ReactNode; placeholder: string; onValidate: (input: string) => boolean } => {
  const hasKey = 'key' in entity;
  const placeholder = hasKey ? entity.key : entity.name;
  return {
    label: label || (
      <Message
        id="ui.confirmation.typeToConfirm"
        values={{
          type,
          resource: () => (
            <CopyToClipboard text={placeholder}>
              <Button size="small">
                {hasKey ? (
                  <Code style={{ backgroundColor: 'transparent', padding: 0 }}>{entity.key}</Code>
                ) : (
                  entity.name
                )}
                <Icon name="copy-clipboard" size="small" slot="end-icon" />
              </Button>
            </CopyToClipboard>
          ),
        }}
      />
    ),
    placeholder,
    onValidate: (input: string) =>
      isResourceConfirmationValid({ input, name: entity.name, key: hasKey ? entity.key : undefined }),
  };
};

export const environmentValidation = (environment: Environment) => {
  return validation(msg('ui.confirmation.environmentType'), environment);
};

export const flagValidation = (flag: Flag) => {
  return validation(msg('ui.confirmation.flagType'), flag);
};

export const featureFlagValidation = (featureFlag: Pick<FeatureFlag, 'name' | 'key'>) => {
  return validation(msg('ui.confirmation.flagType'), featureFlag);
};

export const segmentValidation = (segment: Segment) => {
  return validation(msg('ui.confirmation.segmentType'), segment);
};

export const aiConfigValiation = (aiConfig: AiConfig) => {
  return validation(msg('ui.confirmation.configType'), aiConfig);
};

export const experimentValidation = (experiment: ExperimentApprovalResource) => {
  return validation(msg('ui.confirmation.experimentType'), experiment);
};

export const sdkKeyValidation = (sdkKeyKey: string) => {
  return validation(msg('ui.confirmation.keyType'), { key: sdkKeyKey });
};

export const revokeValidation = () => ({
  label: msg('ui.confirmation.typeYesToConfirm'),
  placeholder: 'yes',
  onValidate: (input: string) => input.toLowerCase() === 'yes',
});

export const racValidation = (rac: RelayAutoConfig) => {
  return validation(msg('ui.confirmation.relayConfigurationType'), rac);
};

export const isValidResourceConfirmation = (key?: string, name?: string) =>
  predicateFor(
    (input: string) => !isResourceConfirmationValid({ input, key, name }),
    () => msg('ui.confirmation.incorrectKey'),
  );
